import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, IndianRupee } from "lucide-react";
import { payWithRazorpay } from "@/lib/razorpay";
import { testIds } from "@/lib/api";
import { useCurrency } from "@/lib/currency";

const initial = { name: "", email: "", phone: "", experience_level: "beginner", notes: "" };

export default function BootcampRegisterDialog({ bootcamp, onClose, onSuccess }) {
  const [form, setForm] = useState(initial);
  const [rzpLoading, setRzpLoading] = useState(false);
  const { format, isUsd } = useCurrency();

  const open = Boolean(bootcamp);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const validate = () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return false;
    }
    return true;
  };

  const payViaRazorpay = async () => {
    if (!validate()) return;
    setRzpLoading(true);
    try {
      await payWithRazorpay({
        orderPayload: { order_type: "bootcamp", bootcamp_id: bootcamp.id, ...form },
        prefill: { name: form.name, email: form.email, contact: form.phone || "" },
        onPaid: ({ amount }) => {
          window.location.href = `/payment-success?rzp=1&type=bootcamp&amount=${(amount / 100).toFixed(2)}`;
        },
        onError: (msg) => toast.error(msg),
        onDismiss: () => setRzpLoading(false),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Razorpay checkout.");
    } finally {
      setRzpLoading(false);
    }
  };

  const close = () => {
    setForm(initial);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        data-testid={testIds.bootcamps.dialog}
        className="sm:max-w-[520px] bg-[#F7F5F0] border-[#E5E2D9] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-medium tracking-tight text-[#2D372B]">
            Register for {bootcamp?.title}
          </DialogTitle>
          <DialogDescription className="text-[#5C665A]">
            Pay securely via UPI, card or netbanking.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => { e.preventDefault(); payViaRazorpay(); }} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-[#2D372B]">Full name</Label>
              <Input
                id="name"
                data-testid={testIds.bootcamps.inputName}
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Doe"
                className="bg-white border-[#E5E2D9]"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#2D372B]">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid={testIds.bootcamps.inputEmail}
                value={form.email}
                onChange={set("email")}
                placeholder="jane@example.com"
                className="bg-white border-[#E5E2D9]"
                required
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone" className="text-[#2D372B]">Phone (optional)</Label>
              <Input
                id="phone"
                data-testid={testIds.bootcamps.inputPhone}
                value={form.phone}
                onChange={set("phone")}
                placeholder="+1 555 000 1234"
                className="bg-white border-[#E5E2D9]"
              />
            </div>
            <div>
              <Label className="text-[#2D372B]">Experience</Label>
              <Select value={form.experience_level} onValueChange={(v) => setForm((f) => ({ ...f, experience_level: v }))}>
                <SelectTrigger data-testid={testIds.bootcamps.inputExperience} className="bg-white border-[#E5E2D9]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="notes" className="text-[#2D372B]">Anything I should know?</Label>
            <Textarea
              id="notes"
              data-testid={testIds.bootcamps.inputNotes}
              value={form.notes}
              onChange={set("notes")}
              placeholder="Injuries, goals, questions…"
              className="bg-white border-[#E5E2D9] min-h-[90px]"
            />
          </div>

          <div className="flex flex-col gap-3 pt-2">
            <div>
              <div className="overline">Total</div>
              <div className="font-serif-display text-3xl text-[#2D372B]">
                {format(bootcamp?.price ?? 0)}
              </div>
              {!isUsd && (
                <div className="text-xs text-[#5C665A] mt-1">
                  Charged in ₹ INR at the live exchange rate
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="bootcamp-razorpay-btn"
              onClick={payViaRazorpay}
              disabled={rzpLoading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#89937C] text-white font-medium hover:bg-[#737D67] transition-colors disabled:opacity-60"
            >
              {rzpLoading ? <Loader2 size={16} className="animate-spin" /> : <IndianRupee size={16} />}
              Pay — UPI · Card · NetBanking
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
